import { useEffect, useRef, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtDate, fmtPercent } from "@/lib/format";
import { toast } from "sonner";
import {
  Building2, MapPin, Landmark, FileText, ShieldCheck, Palette, Pencil, Save,
  X, Loader2, Upload, Trash2, ShieldAlert,
} from "lucide-react";

// Fields editable via PUT /settings/company (matches CompanyUpdate schema).
const TEXT_FIELDS = [
  { name: "legalName", label: "Legal Name" },
  { name: "tradeName", label: "Trade Name" },
  { name: "tagline", label: "Tagline" },
  { name: "slogan", label: "Slogan" },
  { name: "gstin", label: "GSTIN" },
  { name: "pan", label: "PAN" },
  { name: "cin", label: "CIN" },
  { name: "fssaiLicenceNo", label: "FSSAI Licence No" },
  { name: "fssaiLicenceExpiry", label: "FSSAI Expiry (YYYY-MM-DD)" },
  { name: "invoicePrefix", label: "Invoice Prefix" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "website", label: "Website" },
  { name: "deliveryContactPerson", label: "Delivery Contact" },
  { name: "deliveryPhone", label: "Delivery Phone" },
  { name: "deliveryWindow", label: "Delivery Window" },
  { name: "bankName", label: "Bank Name" },
  { name: "bankAccountNo", label: "Account No" },
  { name: "bankIfsc", label: "IFSC" },
  { name: "bankBranch", label: "Branch" },
];
const AREA_FIELDS = [
  { name: "registeredAddress", label: "Registered Address" },
  { name: "deliveryAddress", label: "Delivery Address" },
  { name: "deliveryInstructions", label: "Delivery Instructions" },
  { name: "poTerms", label: "Purchase Order Terms" },
];

function Field({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-stone-100 py-2.5 last:border-0">
      <div className="col-span-1 text-xs font-medium uppercase tracking-wide text-stone-500">{label}</div>
      <div className="col-span-2 whitespace-pre-line text-sm text-stone-800">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-amber-50 text-amber-700"><Icon className="h-4 w-4" /></div>
        <h3 className="font-display text-base font-semibold text-stone-900">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function AssetTile({ label, kind, hasAsset, dataUri, canEdit, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/settings/company/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, `Could not upload ${label.toLowerCase()}`)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setConfirmDel(false); setBusy(true);
    try {
      await api.delete(`/settings/company/${kind}`);
      toast.success(`${label} removed`);
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, `Could not remove ${label.toLowerCase()}`)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-stone-200 p-3" data-testid={`asset-${kind}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</div>
      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-md border border-dashed border-stone-300 bg-stone-50">
        {hasAsset && dataUri ? <img src={dataUri} alt={label} className="h-full w-full object-contain" data-testid={`asset-img-${kind}`} />
          : <span className="text-[11px] text-muted-foreground">Not set</span>}
      </div>
      {canEdit && (
        <div className="flex gap-1.5">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`asset-input-${kind}`}
            onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`asset-upload-${kind}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} {hasAsset ? "Replace" : "Upload"}
          </Button>
          {hasAsset && (
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-rose-600" disabled={busy} onClick={() => setConfirmDel(true)} data-testid={`asset-delete-${kind}`}>
              <Trash2 className="h-3 w-3" /> Remove
            </Button>
          )}
        </div>
      )}
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {label}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the {label.toLowerCase()} from the company profile used on ERP documents.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`asset-delete-cancel-${kind}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={remove} data-testid={`asset-delete-confirm-${kind}`}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function CompanyProfile() {
  const { data: c, isLoading, isError, error, refetch } = useResource("/settings/company");
  const { role } = useAuth();
  const canEdit = role === "management" || role === "super_admin";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  useEffect(() => {
    if (c && editing) {
      const base = {};
      [...TEXT_FIELDS, ...AREA_FIELDS].forEach((f) => { base[f.name] = c[f.name] ?? ""; });
      base.costVarianceTolerancePct = c.costVarianceTolerancePct ?? "";
      setForm(base);
    }
  }, [editing, c]);

  if (isLoading) return <div className="space-y-6"><PageHeader title="Company Profile" /><LoadingState /></div>;
  if (isError) return <div className="space-y-6"><PageHeader title="Company Profile" /><ErrorState message={apiErrorMessage(error)} onRetry={refetch} /></div>;

  const setF = (n, v) => setForm((s) => ({ ...s, [n]: v }));

  const save = async () => {
    setConfirmSave(false); setSaving(true);
    try {
      const payload = { ...form };
      if (payload.costVarianceTolerancePct === "") delete payload.costVarianceTolerancePct;
      else payload.costVarianceTolerancePct = Number(payload.costVarianceTolerancePct);
      await api.put("/settings/company", payload);
      toast.success("Company profile updated");
      setEditing(false);
      refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Company Profile" subtitle="Master company details used across ERP documents & PDFs.">
        {canEdit && !editing && (
          <Button data-testid="company-edit-btn" onClick={() => setEditing(true)} className="gap-2 bg-amber-700 hover:bg-amber-800">
            <Pencil className="h-4 w-4" /> Edit Profile
          </Button>
        )}
        {editing && (
          <>
            <Button data-testid="company-cancel-btn" variant="outline" className="gap-2" onClick={() => setEditing(false)}><X className="h-4 w-4" /> Cancel</Button>
            <Button data-testid="company-save-btn" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={() => setConfirmSave(true)}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </Button>
          </>
        )}
      </PageHeader>

      <div className="flex items-center gap-4 rounded-xl border border-stone-200 bg-gradient-to-br from-white to-amber-50/40 p-5 shadow-sm">
        {c.hasLogo && c.logoDataUri ? (
          <img src={c.logoDataUri} alt="logo" className="h-16 w-16 rounded-lg object-contain" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-amber-700 text-white"><Building2 className="h-7 w-7" /></div>
        )}
        <div>
          <div className="font-display text-xl font-bold text-stone-900">{c.tradeName}</div>
          <div className="text-sm text-stone-600">{c.legalName}</div>
          {c.tagline && <div className="mt-1 text-xs italic text-amber-700">{c.tagline}</div>}
        </div>
      </div>

      {editing ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section icon={FileText} title="Company Details">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {TEXT_FIELDS.map((f) => (
                <div key={f.name} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Input data-testid={`company-field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value)} className="h-9 text-sm" />
                </div>
              ))}
              <div className="space-y-1">
                <Label className="text-xs">Cost Variance Tolerance %</Label>
                <Input data-testid="company-field-costVarianceTolerancePct" type="number" value={form.costVarianceTolerancePct ?? ""} onChange={(e) => setF("costVarianceTolerancePct", e.target.value)} className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">PO Rounding Tolerance (₹)</Label>
                <Input data-testid="company-field-poRoundingTolerance" type="number" step="0.01" value={form.poRoundingTolerance ?? ""} onChange={(e) => setF("poRoundingTolerance", e.target.value)} className="h-9 text-sm" placeholder="50" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Sales Bill Value Tolerance (₹)</Label>
                <Input data-testid="company-field-salesRoundingTolerance" type="number" step="0.01" value={form.salesRoundingTolerance ?? ""} onChange={(e) => setF("salesRoundingTolerance", e.target.value)} className="h-9 text-sm" placeholder="10" />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              {AREA_FIELDS.map((f) => (
                <div key={f.name} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Textarea data-testid={`company-field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value)} className="min-h-[60px] text-sm" />
                </div>
              ))}
            </div>
          </Section>

          <Section icon={Palette} title="Branding Assets">
            <p className="mb-3 text-xs text-muted-foreground">Uploaded assets appear on the PO PDF and other branded documents.</p>
            <div className="flex flex-wrap gap-3">
              <AssetTile label="Logo" kind="logo" hasAsset={c.hasLogo} dataUri={c.logoDataUri} canEdit={canEdit} onChanged={refetch} />
              <AssetTile label="Signature" kind="signature" hasAsset={c.hasSignature} dataUri={c.signatureDataUri} canEdit={canEdit} onChanged={refetch} />
              <AssetTile label="Stamp" kind="stamp" hasAsset={c.hasStamp} dataUri={c.stampDataUri} canEdit={canEdit} onChanged={refetch} />
            </div>
          </Section>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section icon={FileText} title="Identity & Licences">
            <Field label="Legal Name" value={c.legalName} />
            <Field label="Trade Name" value={c.tradeName} />
            <Field label="Slogan" value={c.slogan} />
            <Field label="GSTIN" value={c.gstin} />
            <Field label="PAN" value={c.pan} />
            <Field label="CIN" value={c.cin} />
            <Field label="FSSAI Licence" value={c.fssaiLicenceNo} />
            <Field label="FSSAI Expiry" value={c.fssaiLicenceExpiry ? fmtDate(c.fssaiLicenceExpiry) : ""} />
            <Field label="Invoice Prefix" value={c.invoicePrefix} />
          </Section>
          <Section icon={MapPin} title="Contact & Addresses">
            <Field label="Email" value={c.email} />
            <Field label="Phone" value={c.phone} />
            <Field label="Website" value={c.website} />
            <Field label="Registered Address" value={c.registeredAddress} />
            <Field label="Delivery Address" value={c.deliveryAddress} />
            <Field label="Delivery Contact" value={c.deliveryContactPerson} />
            <Field label="Delivery Window" value={c.deliveryWindow} />
          </Section>
          <Section icon={Landmark} title="Bank Details">
            <Field label="Bank Name" value={c.bankName} />
            <Field label="Account No" value={c.bankAccountNo} />
            <Field label="IFSC" value={c.bankIfsc} />
            <Field label="Branch" value={c.bankBranch} />
          </Section>
          <Section icon={ShieldCheck} title="Costing Tolerances">
            <Field label="Default Tolerance" value={fmtPercent(c.costVarianceTolerancePct)} />
            {c.costTolerances && Object.entries(c.costTolerances).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, " ")} value={fmtPercent(v)} />
            ))}
          </Section>
          <Section icon={FileText} title="Purchase Order Terms">
            <div className="whitespace-pre-line text-sm text-stone-700">{c.poTerms || "—"}</div>
          </Section>
          <Section icon={Palette} title="Branding Assets">
            <div className="flex flex-wrap gap-3">
              <AssetTile label="Logo" kind="logo" hasAsset={c.hasLogo} dataUri={c.logoDataUri} canEdit={canEdit} onChanged={refetch} />
              <AssetTile label="Signature" kind="signature" hasAsset={c.hasSignature} dataUri={c.signatureDataUri} canEdit={canEdit} onChanged={refetch} />
              <AssetTile label="Stamp" kind="stamp" hasAsset={c.hasStamp} dataUri={c.stampDataUri} canEdit={canEdit} onChanged={refetch} />
            </div>
          </Section>
        </div>
      )}

      <AlertDialog open={confirmSave} onOpenChange={setConfirmSave}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Save company profile</AlertDialogTitle>
            <AlertDialogDescription>
              This updates the shared Company Master used across all ERP documents (PO PDF, invoices) on both web and mobile. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-save-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="company-save-confirm" className="bg-emerald-600 hover:bg-emerald-700" onClick={save}>Yes, save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
